import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, LOGO_BASE_URL } from '../../../apiConfig';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, ChevronDown, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';
import useBreakpoint from '../../../hooks/useBreakpoint';

export default function UpcomingMatches() {
    const [matches, setMatches] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [selectedDiv, setSelectedDiv] = useState("");
    const [loading, setLoading] = useState(true);
    const [index, setIndex] = useState(0);
    const { isMobile, isTablet } = useBreakpoint();

    // 1. Fetch Divisions on mount
    useEffect(() => {
        let isMounted = true;
        axios.get(`${API_BASE_URL}/currentDivision`)
            .then(res => {
                if (isMounted && res.data && res.data.length > 0) {
                    setDivisions(res.data);
                    // Default to the first division's PCID
                    setSelectedDiv(res.data[0].PCID);
                } else if (isMounted) {
                    // Fallback
                    setSelectedDiv("137680");
                }
            })
            .catch(err => {
                console.error("Failed to fetch divisions:", err);
                if (isMounted) setSelectedDiv("137680");
            });
        return () => { isMounted = false; };
    }, []);

    // 2. Fetch Matches whenever selectedDiv changes
    useEffect(() => {
        if (!selectedDiv) return;
        
        let isMounted = true;
        setLoading(true);
        setIndex(0); // Reset slider to start
        
        axios.get(`${API_BASE_URL}/divisionFixture/${selectedDiv}`)
            .then(res => {
                if (isMounted && res.data) {
                    setMatches(res.data.slice(0, 9));
                }
            })
            .catch(err => console.error("Failed to fetch matches:", err))
            .finally(() => {
                if (isMounted) setLoading(false);
            });
            
        return () => { isMounted = false; };
    }, [selectedDiv]);

    if (!loading && matches.length === 0 && divisions.length === 0) return null;

    // How many cards visible per page
    const perPage = isMobile ? 1 : isTablet ? 2 : 3;
    const data = matches;
    const visible = data.slice(index, index + perPage);
    const canPrev = index > 0;
    const canNext = index + perPage < data.length;

    const FONT_RAJ = "'Rajdhani', sans-serif";

    return (
        <section style={{
            padding: isMobile ? '24px 0' : '40px 0',
            maxWidth: 'var(--maxWidth)',
            margin: '0 auto',
            width: '100%',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                padding: isMobile ? '0 16px' : '0 24px',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '24px' }}>
                    <h2 style={{
                        fontFamily: 'var(--fontDisplay)',
                        fontSize: isMobile ? '22px' : '28px',
                        fontWeight: 800,
                        lineHeight: '1.2',
                        color: '#000',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        Upcoming Matches
                    </h2>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', flexWrap: 'wrap' }}>
                    {/* Division dropdown moved to right side */}
                    {divisions.length > 0 && (
                        <div style={{
                            boxSizing: 'border-box', display: 'flex', flexDirection: 'row',
                            justifyContent: 'space-between', alignItems: 'center',
                            padding: isMobile ? '8px 16px' : '12px 24px',
                            gap: '16px',
                            width: isMobile ? '140px' : '176px',
                            height: isMobile ? '38px' : '44px',
                            border: '1px solid #060D8A', position: 'relative', flexShrink: 0,
                        }}>
                            <select
                                value={selectedDiv}
                                onChange={(e) => setSelectedDiv(e.target.value)}
                                style={{
                                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                                    opacity: 0, cursor: 'pointer', zIndex: 2,
                                }}>
                                {divisions.map((d) => (
                                    <option key={d.PCID} value={d.PCID}>{d.name}</option>
                                ))}
                            </select>
                            <span style={{ 
                                fontFamily: FONT_RAJ, fontWeight: 700, 
                                fontSize: isMobile ? '14px' : '16px', 
                                color: '#060D8A', pointerEvents: 'none',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}>
                                {divisions.find(d => d.PCID == selectedDiv)?.name || 'Division'}
                            </span>
                            <ChevronDown size={14} color="#060D8A" style={{ pointerEvents: 'none', flexShrink: 0 }} />
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => canPrev && setIndex(i => i - perPage)}
                            style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #e5e7eb',
                                background: canPrev ? '#111' : '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: canPrev ? 'pointer' : 'default', transition: 'all 0.2s',
                            }}>
                            <ChevronLeft size={20} color={canPrev ? '#fff' : '#bbb'} />
                        </button>
                        <button
                            onClick={() => canNext && setIndex(i => i + perPage)}
                            style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                background: canNext ? '#111' : '#fff',
                                border: canNext ? 'none' : '1.5px solid #e5e7eb',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: canNext ? 'pointer' : 'default', transition: 'all 0.2s',
                            }}>
                            <ChevronRight size={20} color={canNext ? '#fff' : '#bbb'} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Cards Row */}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                padding: isMobile ? '0 16px' : '0 24px',
                gap: isMobile ? '16px' : '32px',
                width: '100%',
                boxSizing: 'border-box',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.3s'
            }}>
                {visible.length > 0 ? visible.map((match, i) => (
                    <MatchCard 
                        key={i} 
                        match={match} 
                        isMobile={isMobile} 
                        isTablet={isTablet} 
                        selectedDivName={divisions.find(d => d.PCID == selectedDiv)?.name}
                    />
                )) : <MatchEmptyState type="fixtures" isMobile={isMobile} />}
            </div>
        </section>
    );
}

function MatchEmptyState({ type, isMobile }) {
    const isFixtures = type === 'fixtures';
    return (
        <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '40px 20px' : '80px 40px',
            background: 'rgba(6, 13, 138, 0.02)',
            border: '2px dashed rgba(6, 13, 138, 0.1)',
            borderRadius: '4px',
            textAlign: 'center',
            boxSizing: 'border-box'
        }}>
            <div style={{ opacity: 1, marginBottom: '16px' }}>
                {isFixtures ? <Calendar size={64} color="#060D8A" /> : <Trophy size={64} color="#060D8A" />}
            </div>
            <h3 style={{ 
                fontFamily: 'var(--fontDisplay)', 
                fontWeight: 900, 
                fontSize: isMobile ? '20px' : '24px', 
                color: '#060D8A', 
                margin: '0 0 8px 0', 
                textTransform: 'uppercase',
                fontStyle: 'italic'
            }}>
                {isFixtures ? 'No Upcoming Fixtures' : 'No Results Found'}
            </h3>
            <p style={{ 
                fontFamily: 'var(--fontDisplay)', 
                fontSize: isMobile ? '13px' : '15px', 
                color: 'rgba(0,0,0,0.5)', 
                margin: 0,
                fontWeight: 600,
                textTransform: 'uppercase'
            }}>
                {isFixtures ? 'The schedule for this season is yet to be announced' : 'Stay tuned for match updates'}
            </p>
        </div>
    );
}

function MatchCard({ match, isMobile, isTablet, selectedDivName }) {
    const homeName = match.home_club_name || 'Home Team';
    const awayName = match.away_club_name || 'Away Team';
    const homeLogo = match.home_team_logo;
    const awayLogo = match.away_team_logo;
    const accentYellow = '#E7C424';

    const [imgErrorHome, setImgErrorHome] = useState(false);
    const [imgErrorAway, setImgErrorAway] = useState(false);

    return (
        <motion.div 
            whileHover={{ y: -8, boxShadow: '0 12px 24px rgba(0,0,0,0.3)' }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            style={{
                flex: 1,
                minWidth: 0,
                height: 'auto',
                background: '#040960',
                border: '1px solid rgba(255,255,255,0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
            }}
        >
            {/* Header */}
            <div style={{
                padding: isMobile ? '12px 16px' : '14px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: '#fff',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                gap: '10px',
                position: 'relative',
                zIndex: 1,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Calendar size={12} color="#fff" style={{ flexShrink: 0 }} />
                    <span style={{
                        fontSize: '11px', fontWeight: 600,
                        fontFamily: 'var(--fontDisplay)', whiteSpace: 'nowrap',
                        color: '#fff', textTransform: 'uppercase'
                    }}>
                        {match.match_date || '01 June 2025'}
                    </span>
                </div>
                
                <span style={{
                    fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: '1px', color: '#040960', fontFamily: 'var(--fontDisplay)',
                    background: '#fff', 
                    padding: '2px 10px', borderRadius: '20px',
                }}>
                    {match.division_name || selectedDivName || 'Premier'}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', minWidth: 0 }}>
                    <Clock size={12} color="#fff" style={{ flexShrink: 0 }} />
                    <span style={{ 
                        fontSize: '11px', fontWeight: 600, 
                        fontFamily: 'var(--fontDisplay)', whiteSpace: 'nowrap',
                        color: '#fff',
                    }}>
                        {match.match_time || '11:30AM'}
                    </span>
                </div>
            </div>

            {/* Body */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 60px 1fr',
                alignItems: 'center',
                padding: isMobile ? '20px 10px' : '30px 20px',
                flex: 1,
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Home Team */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                    {/* Logo Home */}
                    <div style={{
                        width: isMobile ? '50px' : '64px',
                        height: isMobile ? '50px' : '64px',
                        background: '#fff',
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: '10px',
                        border: '2px solid rgba(255,255,255,0.2)',
                        overflow: 'hidden'
                    }}>
                        {(homeLogo && !imgErrorHome) ? (
                            <img 
                                src={`${LOGO_BASE_URL}/${homeLogo}`} 
                                alt={homeName}
                                onError={() => setImgErrorHome(true)}
                                style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Trophy size={20} color="#040960" />
                        )}
                    </div>
                    <span style={{
                        fontSize: isMobile ? '13px' : '15px', 
                        fontWeight: 700, 
                        color: '#fff',
                        fontFamily: 'var(--fontDisplay)', 
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                    }}>
                        {homeName}
                    </span>
                </div>
  
                {/* VS Section */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                        fontSize: isMobile ? '24px' : '32px', fontWeight: 900, color: '#fff',
                        fontFamily: 'var(--fontDisplay)', fontStyle: 'italic',
                        lineHeight: 1, userSelect: 'none', textAlign: 'center',
                    }}>
                        V/S
                    </div>
                </div>
  
                {/* Away Team */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                    {/* Logo Away */}
                    <div style={{
                        width: isMobile ? '50px' : '64px',
                        height: isMobile ? '50px' : '64px',
                        background: '#fff',
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: '10px',
                        border: '2px solid rgba(255,255,255,0.2)',
                        overflow: 'hidden'
                    }}>
                        {(awayLogo && !imgErrorAway) ? (
                            <img 
                                src={`${LOGO_BASE_URL}/${awayLogo}`} 
                                alt={awayName}
                                onError={() => setImgErrorAway(true)}
                                style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Trophy size={20} color="#040960" />
                        )}
                    </div>
                    <span style={{
                        fontSize: isMobile ? '13px' : '15px', 
                        fontWeight: 700, 
                        color: '#fff',
                        fontFamily: 'var(--fontDisplay)', 
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        lineHeight: 1.1,
                        letterSpacing: '0.5px',
                    }}>
                        {awayName}
                    </span>
                </div>
            </div>

            {/* Footer */}
            <div style={{
                padding: '10px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(0,0,0,0.1)',
            }}>
                <MapPin size={12} color="#fff" style={{ flexShrink: 0, opacity: 0.7 }} />
                <span style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.7)', 
                    fontFamily: 'var(--fontDisplay)',
                    fontWeight: 500,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {match.ground_name || match.venue || 'TBD'}
                </span>
            </div>
        </motion.div>
    );
}
